import { GenericError } from '@/models/generic-error';
import { Tile } from '@/models/tiles';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { GameRoundQueryKeys } from '../../queryKeys';
import { UseGameRoundTilesProps, UseGameRoundTilesQueryKey } from './props';
import { fetchGameRoundTiles } from './service';

export function useGameRoundTiles(options?: UseGameRoundTilesProps) {
  const { roundId } = options ?? {};
  const queryKey: UseGameRoundTilesQueryKey = [
    GameRoundQueryKeys.Tiles,
    {
      roundId: roundId as string,
    },
  ];

  const { data, fetchStatus, ...infinityQuery } = useQuery<
    Tile[],
    AxiosError<GenericError>,
    Tile[],
    UseGameRoundTilesQueryKey
  >({
    queryKey,
    queryFn: fetchGameRoundTiles,
    enabled: !!roundId,
    staleTime: 5 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const finalTiles: Tile[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const copies = Array.from({ length: 8 }, (_, copyIndex) => {
      if (copyIndex === 0)
        return data.map(tile => ({
          ...tile,
          type: tile.type,
          number: Number(tile.multiplier),
        }));

      return data.map(tile => ({
        ...tile,
        id: `${tile.id}-copy${copyIndex}` as any,
        type: tile.type,
        number: Number(tile.multiplier),
      }));
    });

    return copies.flat();
  }, [data]);

  return {
    ...infinityQuery,
    tiles: finalTiles,
    originalTiles: data,
    isLoading: infinityQuery.isPending && fetchStatus !== 'idle',
    error: infinityQuery.error as AxiosError<GenericError>,
  };
}
